import type { RegisteredForm, RegisteredFormWithRole, Snapshot } from 'contexts/FormRegistryProvider';
import { FORM_ROLE_ITEMS } from 'contexts/FormRegistryProvider';
import type { DataSchema } from 'types';
import { cloneDeep } from 'lodash';

export const convertSnapshotToForm = <T extends keyof P, P extends DataSchema>(snapshot: Snapshot<T, P>): RegisteredFormWithRole | undefined => {
  if (!snapshot.meta?.formIdentifier)
    throw new Error('Snapshot is missing formIdentifier: required to identify the form in the registry and link snapshot data to the correct form.');

  const api: RegisteredForm['api'] = {
    trigger: async () => true,
    getValues: () => snapshot.values,
    getDirtyFields: () => snapshot.dirtyFields,
    isDirty: () => snapshot.isDirty,
    resetSavedForm: () => undefined,
  };

  return {
    id: '',
    role: FORM_ROLE_ITEMS,
    api,
    data: {
      ...snapshot.meta,
      formIdentifier: snapshot.meta?.formIdentifier,
      selectedRows: snapshot.selectedRows,
      totalRows: snapshot.totalRows,
    },
    dataSchema: snapshot.dataSchema,
    mounted: false,
  };
};

export const mergeRegistryAndSnapshots = <T extends keyof P, P extends DataSchema>(
  registryForms: RegisteredFormWithRole[] | undefined,
  snapshots: Snapshot<T, P>[]
): RegisteredFormWithRole[] => {
  const mounted = new Map<string, RegisteredFormWithRole>();
  if (registryForms) {
    for (const f of registryForms) {
      if (!f.mounted) continue;

      const id = f.data?.formIdentifier;
      if (id) mounted.set(id, f);
    }
  }

  const forms: RegisteredFormWithRole[] = [];
  for (const snap of snapshots) {
    const formIdentifier = snap.meta?.formIdentifier;
    if (!formIdentifier) continue;
    if (mounted.has(formIdentifier)) continue;
    const form = convertSnapshotToForm(snap);
    if (form) forms.push(form);
  }

  return registryForms ? [...registryForms, ...forms] : forms;
};

export const copySnapshot = <T extends keyof P, P extends DataSchema>(source: Snapshot<T, P>): Snapshot<T, P> => {
  return cloneDeep(source);
};
